import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import api from '../services/api';
import SEO from '../components/SEO';

function Fees() {
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState('All');

  useEffect(() => {
    fetchFees();
  }, []);

  const fetchFees = async () => {
    try {
      const response = await api.get('/fees');
      console.log('💰 Fetched fees:', response.data);
      setFees(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching fees:', error);
      setFees([
        { id: 1, className: 'Class 10', feeType: 'Tuition', amount: 1500, frequency: 'Monthly', description: 'Monthly tuition fee', isActive: true },
        { id: 2, className: 'Class 10', feeType: 'Annual', amount: 5000, frequency: 'Yearly', description: 'Annual charges', isActive: true }
      ]);
      setLoading(false);
    }
  };

  const classOptions = ['All', 'Pre-Nursery', 'Nursery', 'KG', 'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5', 
                        'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10', 'Class 11', 'Class 12'];

  const filteredFees = selectedClass === 'All' ? fees : fees.filter(fee => fee.className === selectedClass);
  
  const totalAmount = filteredFees.reduce((sum, fee) => sum + parseFloat(fee.amount || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
        <div className="text-center text-xl text-blue-900">Loading fee structure...</div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="Fee Structure 2026-27 | Vidya Vatika World School Panipat"
        description="Transparent and affordable fee structure for all classes Nursery to Class 12. View detailed fee breakdown online."
        keywords="school fees Panipat, fee structure CBSE, affordable schooling, class-wise fees Haryana, admission fees 2026"
        canonicalUrl="/fees"
        ogImage="/images/og-image.jpg"
      />
      
      <div className="pt-20 min-h-screen bg-gray-50">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-5xl font-bold font-display mb-4"
          >
            Fee Structure
          </motion.h1>
          <p className="text-xl text-blue-100">Transparent and affordable education for all</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <label className="font-semibold text-gray-700">Select Class:</label>
              <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}
                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-900">
                {classOptions.map(cls => <option key={cls} value={cls}>{cls}</option>)}
              </select>
            </div>
            <div className="bg-green-50 px-6 py-3 rounded-lg border border-green-200">
              <span className="text-sm text-gray-600">Total Fees: </span>
              <span className="text-2xl font-bold text-green-700">₹{totalAmount.toLocaleString('en-IN')}</span>
            </div>
          </div>
        </div>

        {/* Fees Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredFees.map((fee) => (
            <div key={fee.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all">
              <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-bold text-lg">{fee.className}</h3>
                    <p className="text-blue-100 text-sm">{fee.feeType} Fee</p>
                  </div>
                  <div className="text-3xl opacity-75">₹</div>
                </div>
              </div>
              <div className="p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Amount:</span>
                  <span className="text-2xl font-bold text-blue-900">₹{parseFloat(fee.amount).toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Frequency:</span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">{fee.frequency}</span>
                </div>
                {fee.description && (
                  <p className="text-sm text-gray-600 mb-3">{fee.description}</p>
                )}
                <div className="pt-3 border-t">
                  <button className="w-full bg-blue-900 text-white py-2 rounded-lg hover:bg-blue-800 transition text-sm font-semibold">
                    Enroll Now
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredFees.length === 0 && (
          <div className="text-center text-gray-600 mt-12 py-8">
            <p className="text-xl">No fee structure available</p>
            <p className="text-sm mt-2">Please check back later or contact the school office</p>
          </div>
        )}
      </div>
    </div>
    </>
  );
}

export default Fees;
